
import Integer from './Integer.js'
import { isInteger } from './is.js'

export function throwIfNotWithin (value, availableValues) {
  if (availableValues !== undefined && availableValues.length) {
    let found = false

    for (const availableValue of availableValues) {
      if (value === availableValue) {
        found = true
        break
      }
    }

    if (!found) {
      throw new RangeError(`${value} is not within [${availableValues.join(', ')}]`)
    }
  }
}

export function throwIfNoInstanceOf (value, availableValues) {
  if (availableValues !== undefined && availableValues.length) {
    let found = false

    for (const availableValue of availableValues) {
      if (value instanceof availableValue) {
        found = true
        break
      }
    }

    if (!found) {
      throw new RangeError(`${value} is not within [${availableValues.join(', ')}]`)
    }
  }
}

export function getOption (opts, prop, type, values, fallback) {
  if (typeof opts !== 'object') {
    throw new TypeError('Options must be an object')
  }
  let value = opts[prop]
  if (value !== undefined) {
    if (type !== 'boolean' && type !== 'string') {
      throw new TypeError('invalid type')
    }

    if (type === 'boolean') {
      value = Boolean(value)
    }

    if (type === 'string') {
      value = String(value)
    }

    throwIfNotWithin(value, values)
    return value
  }

  return fallback
}

export function getBooleanOption (opts, prop, fallback) {
  if (typeof opts !== 'object') {
    throw new TypeError('Options must be an object')
  }

  const value = opts[prop]

  if (value !== undefined) {
    return Boolean(value)
  }

  return fallback
}

export function getStringOption (opts, prop, values, fallback) {
  if (typeof opts !== 'object') {
    throw new TypeError('Options must be an object')
  }

  let value = opts[prop]

  if (value !== undefined) {
    value = String(value)
    throwIfNotWithin(value, values)
    return value
  }

  return fallback
}

export function getObjectOption (opts, prop, values, fallback) {
  if (typeof opts !== 'object') {
    throw new TypeError('Options must be an object')
  }

  const value = opts[prop]

  if (value !== undefined) {
    throwIfNoInstanceOf(value, values)
    return value
  }

  return fallback
}

export function defaultNumberOption (
  val,
  min,
  max,
  fallback
) {
  if (val !== undefined) {
    val = Number(val)
    if (isNaN(val) || val < min || val > max) {
      throw new RangeError(`${val} is outside of range [${min}, ${max}]`)
    }
    return Math.floor(val)
  }
  return fallback
}

export function getNumberOption (
  options,
  property,
  minimum,
  maximum,
  fallback
) {
  const val = options[property]
  // @ts-expect-error
  return defaultNumberOption(val, minimum, maximum, fallback)
}

export function getDaysInYear (year) {
  return 365 + (
    year % 4 === 0 && (
      year % 100 !== 0 ||
      year % 400 === 0
    )
  )
}

export function isLeapYear (year) {
  return (
    year % 4 === 0 && (
      year % 100 !== 0 || year % 400 === 0
    )
  )
}

export function getDaysInMonth (year, month) {
  let days

  switch (month) {
    case 2:
      days = isLeapYear(year) ? 29 : 28
      break
    case 4:
    case 6:
    case 9:
    case 11:
      days = 30
      break
    default:
      days = 31
      break
  }

  return days
}

export function getDayOfWeek (year, month, day) {
  const m = month + (month < 3 ? 10 : -2)
  const Y = year - (month < 3 ? 1 : 0)

  const c = Math.floor(Y / 100)
  const y = Y - c * 100
  const d = day

  const pD = d
  const pM = Math.floor(2.6 * m - 0.2)
  const pY = y + Math.floor(y / 4)
  const pC = Math.floor(c / 4) - 2 * c

  const dow = (pD + pM + pY + pC) % 7

  return dow + (dow <= 0 ? 7 : 0)
}

export function getDayOfYear (year, month, day) {
  let days = day
  let m = month

  while (--m > 0) {
    days += getDaysInMonth(year, m)
  }

  return days
}

export function getWeekOfYear (year, month, day) {
  const doy = getDayOfYear(year, month, day)
  const dow = getDayOfWeek(year, month, day) || 7
  const doj = getDayOfWeek(year, 1, 1)

  const week = Math.floor((doy - dow + 10) / 7)

  if (week < 1) {
    if (doj === 5 || (doj === 6 && isLeapYear(year - 1))) {
      return 53
    } else {
      return 52
    }
  }
  if (week === 53) {
    if ((isLeapYear(year) ? 366 : 365) - doy < 4 - dow) {
      return 1
    }
  }

  return week
}

export function toString (value) {
  if (typeof value === 'symbol') {
    throw new TypeError('Cannot convert a Symbol value to a String')
  }

  return String(value)
}

export function toIntegerThrowOnInfinity (value) {
  const integer = Integer(value)
  if (!Number.isFinite(integer)) {
    throw new RangeError('infinity is out of range')
  }
  return integer
}

export function toPositiveInteger (valueParam, property) {
  const value = Integer(valueParam)
  if (!Number.isFinite(value)) {
    throw new RangeError('infinity is out of range')
  }
  if (value < 1) {
    if (property !== undefined) {
      throw new RangeError(
        `property '${property}' cannot be a a number less than one`
      )
    }
    throw new RangeError(
      'Cannot convert a number less than one to a positive integer'
    )
  }
  return value
}

export function toIntegerWithoutRounding (valueParam) {
  const value = Number(valueParam)
  if (Number.isNaN(value)) return 0
  if (!Number.isFinite(value)) {
    throw new RangeError('infinity is out of range')
  }

  if (!isInteger(value)) {
    throw new RangeError(`unsupported fractional value ${value}`)
  }

  return Integer(value) // ℝ(value) in spec text; converts -0 to 0
}

export function isUndefined (arg) {
  return arg === undefined
}

export function getType (input) {
  if (input === null) {
    return 'Null'
  }

  switch (typeof input) {
    case 'undefined':
      return 'Undefined'
    case 'function':
    case 'object':
      return 'Object'
    case 'number':
      return 'Number'
    case 'boolean':
      return 'Boolean'
    case 'string':
      return 'String'
    case 'symbol':
      return 'Symbol'
    case 'bigint':
      return 'Bigint'
  }
}

// use WeakMap to store the object->key mapping
// so the objects can be garbage collected.
// WeakMap uses a hashtable under the hood, so the lookup
// complexity is almost O(1).
const table = new WeakMap()

// counter of the key
let counter = 0

// A stable hashObject implementation that supports:
// - Fast and ensures unique hashObject properties
// - Handles unserializable values
// - Handles object key ordering
// - Generates short results
//
// This is not a serialization function, and the result is not guaranteed to be
// parsable.
export function hashObject (arg) {
  const type = typeof arg
  const constructor = arg?.constructor
  const isDate = constructor === Date

  let result = ''
  let index

  if (isDate) {
    result = `${arg.toJSON()}`
  } else if (Object(arg) === arg && constructor !== RegExp) {
    // Object/function, not null/date/regexp. Use WeakMap to store the id first.
    // If it's already hashed, directly return the result.
    const resultFromMap = table.get(arg)
    if (resultFromMap) return resultFromMap

    // Store the hashObject first for circular reference detection before entering the
    // recursive `stableHash` calls.
    // For other objects like set and map, we use this id directly as the hashObject.
    result = `-${++counter}-`
    table.set(arg, result)

    if (constructor === Array) {
      // Array.
      result = '['
      index = -1

      while (++index < arg.length) {
        result += `${hashObject(arg[index])},`
      }

      table.set(arg, result)
    }
    if (constructor === Object) {
      // Object, sort keys.
      result = '{'
      const keys = Object.keys(arg).sort()

      while (!isUndefined((index = keys.pop()))) {
        if (!isUndefined(arg[index])) {
          result += `${index}:${hashObject(arg[index])},`
        }
      }

      table.set(arg, result)
    }
  } else {
    result = type === 'symbol'
      ? arg.toString()
      : type === 'string'
        ? JSON.stringify(arg)
        : String(arg)
  }

  return result
}

export function formatSecondsStringPart (
  second,
  millisecond,
  microsecond,
  nanosecond,
  precision
) {
  if (precision === 'minute') return ''

  const secs = ':' + `00${second}`.slice(-2)
  const fractionNumber = millisecond * 1e6 + microsecond * 1e3 + nanosecond
  let fraction

  if (precision === 'auto') {
    if (fractionNumber === 0) return secs
    fraction = `${fractionNumber}`.padStart(9, '0')
    while (fraction[fraction.length - 1] === '0') fraction = fraction.slice(0, -1)
  } else {
    if (precision === 0) return secs
    fraction = `${fractionNumber}`.padStart(9, '0').slice(0, precision)
  }
  return `${secs}.${fraction}`
}

export function toSecondsStringPrecision (options) {
  const smallestUnit = getOption(
    options,
    'smallestUnit',
    'string',
    ['year', 'month', 'week', 'day', 'hour', 'minute', 'second', 'millisecond', 'microsecond', 'nanosecond'],
    'auto'
  )

  if (smallestUnit === 'hour') {
    throw new RangeError(`smallestUnit must be one of minute, second, millisecond, microsecond, nanosecond, not ${smallestUnit}`)
  }

  switch (smallestUnit) {
    case 'minute':
      return { precision: 'minute', unit: 'minute', increment: 1 }
    case 'second':
      return { precision: 0, unit: 'second', increment: 1 }
    case 'millisecond':
      return { precision: 3, unit: 'millisecond', increment: 1 }
    case 'microsecond':
      return { precision: 6, unit: 'microsecond', increment: 1 }
    case 'nanosecond':
      return { precision: 9, unit: 'nanosecond', increment: 1 }
    default: // fall through if option not given
      break
  }

  let digits = options.fractionalDigits
  if (digits === undefined) digits = 'auto'

  if (typeof digits !== 'number') {
    const stringDigits = String(digits)
    if (stringDigits === 'auto') return { precision: 'auto', unit: 'nanosecond', increment: 1 }
    throw new RangeError(`fractionalSecondDigits must be 'auto' or 0 through 9, not ${stringDigits}`)
  }

  const precision = Math.trunc(digits)

  if (!Number.isFinite(precision) || precision < 0 || precision > 9) {
    throw new RangeError(`fractionalSecondDigits must be 'auto' or 0 through 9, not ${digits}`)
  }

  switch (precision) {
    case 0:
      return { precision, unit: 'second', increment: 1 }
    case 1:
    case 2:
    case 3:
      return { precision, unit: 'millisecond', increment: 10 ** (3 - precision) }
    case 4:
    case 5:
    case 6:
      return { precision, unit: 'microsecond', increment: 10 ** (6 - precision) }
    case 7:
    case 8:
    case 9:
      return { precision, unit: 'nanosecond', increment: 10 ** (9 - precision) }
    default:
      throw new RangeError(`fractionalSecondDigits must be 'auto' or 0 through 9, not ${digits}`)
  }
}
